/**
 * Face-relative placement, resolved to template pixels.
 *
 * Pure on purpose. This is the arithmetic that makes `{ face, x, y, scale,
 * rotation }` mean something, and it is the part most worth testing — the DOM
 * compositing around it is a handful of drawImage calls, while a sign error
 * here silently puts somebody's logo on the wrong part of their shirt.
 *
 * Being pure also means the Worker can validate a placement against
 * template-regions.json without a canvas, which is the thing absolute pixel
 * coordinates could never support.
 *
 * Conventions, all of them deliberate:
 *
 *   x, y      0..1 within the face. 0.5, 0.5 is centred. Not pixels, so the
 *             same placement means the same thing on a different template size.
 *   scale     fraction of the face's WIDTH, not its area or its diagonal.
 *             Width because "half as wide as the chest" is a thing a person can
 *             picture, and because faces are square often enough that height
 *             would read the same while behaving differently on limb strips.
 *   rotation  radians, about the placement's own centre. Degrees are UI.
 */
import type { FacePlacement, RegionId } from "./format";
import { REGIONS } from "./regions";

export interface Point {
  x: number;
  y: number;
}

/** A placement in template pixels. Axis-aligned box plus a rotation about it. */
export interface PlacedRect {
  /** Centre, template pixels. */
  cx: number;
  cy: number;
  w: number;
  h: number;
  rotation: number;
}

/**
 * Resolve to template pixels.
 *
 * `aspect` is the source image's width / height. Height follows from width so a
 * logo never stretches — there is deliberately no independent y-scale, because
 * every request for one has really been a request to crop or to redraw.
 */
export function resolvePlacement(p: FacePlacement, aspect: number): PlacedRect {
  const face = REGIONS[p.face];
  const w = p.scale * face.w;
  // A zero or negative aspect means a source that never decoded. Falling back
  // to square keeps this total rather than emitting NaN into a render loop,
  // where it would silently blank the canvas instead of drawing something wrong.
  const h = aspect > 0 && Number.isFinite(aspect) ? w / aspect : w;
  return {
    cx: face.x + p.x * face.w,
    cy: face.y + p.y * face.h,
    w,
    h,
    rotation: p.rotation,
  };
}

/** The four corners, rotation applied, in template pixels. */
export function cornersOf(r: PlacedRect): [Point, Point, Point, Point] {
  const cos = Math.cos(r.rotation);
  const sin = Math.sin(r.rotation);
  const hw = r.w / 2;
  const hh = r.h / 2;
  const at = (dx: number, dy: number): Point => ({
    x: r.cx + dx * cos - dy * sin,
    y: r.cy + dx * sin + dy * cos,
  });
  return [at(-hw, -hh), at(hw, -hh), at(hw, hh), at(-hw, hh)];
}

export interface FaceFit {
  /** True when the whole placement lands inside its face. */
  fits: boolean;
  /** How far the worst corner sits outside the face, in template pixels. */
  overflowPx: number;
}

/**
 * Does this placement fit inside its face?
 *
 * Rendering clips to the face rect, so a placement can never leak onto a
 * neighbouring face or into dead space — that part is structural. What it *can*
 * do is get visibly cut off, and that is what this reports.
 *
 * Rotation is why this exists as a function rather than a rect comparison: a
 * square rotated 45° needs √2 times the room, so a logo that fits at 0° can
 * overflow at an angle without anything about its scale changing.
 *
 * Conservative by design — it measures the source's bounding box, so an image
 * with transparent margins may be reported as clipped when only empty pixels are
 * lost. Warning about a logo that turns out fine is a much cheaper mistake than
 * staying quiet about one that gets its edge sliced off.
 */
export function fitToFace(p: FacePlacement, aspect: number): FaceFit {
  const face = REGIONS[p.face];
  const rect = resolvePlacement(p, aspect);
  let overflow = 0;
  for (const c of cornersOf(rect)) {
    overflow = Math.max(
      overflow,
      face.x - c.x,
      c.x - (face.x + face.w),
      face.y - c.y,
      c.y - (face.y + face.h),
    );
  }
  const overflowPx = Math.max(0, overflow);
  return { fits: overflowPx === 0, overflowPx };
}

/**
 * The largest `scale` that still fits, at the placement's current position and
 * angle. Powers "shrink it to fit" without the user hunting for the number.
 *
 * Solved by proportion rather than search: every corner offset is linear in
 * scale about the centre, so halving the scale halves the overflow. One measure
 * at the current scale gives the ratio directly.
 */
export function scaleToFit(p: FacePlacement, aspect: number): number {
  const fit = fitToFace(p, aspect);
  if (fit.fits) return p.scale;

  const face = REGIONS[p.face];
  const rect = resolvePlacement(p, aspect);
  // Room available from the centre to each face edge, per axis.
  const room = {
    left: rect.cx - face.x,
    right: face.x + face.w - rect.cx,
    top: rect.cy - face.y,
    bottom: face.y + face.h - rect.cy,
  };
  // A centre already outside its own face cannot be fixed by scaling.
  if (room.left < 0 || room.right < 0 || room.top < 0 || room.bottom < 0) return 0;

  let ratio = 1;
  for (const c of cornersOf(rect)) {
    const dx = c.x - rect.cx;
    const dy = c.y - rect.cy;
    if (dx < 0) ratio = Math.min(ratio, room.left / -dx);
    if (dx > 0) ratio = Math.min(ratio, room.right / dx);
    if (dy < 0) ratio = Math.min(ratio, room.top / -dy);
    if (dy > 0) ratio = Math.min(ratio, room.bottom / dy);
  }
  return Math.max(0, p.scale * ratio);
}

/**
 * The face nearest a template point, for snapping a dropped asset.
 *
 * Not `regionAt` — that returns null in the gutters and across the 54.9% of the
 * canvas that never renders, which is precisely where a dropped file is most
 * likely to land. Somebody dragging a logo onto the middle of the template means
 * "put this on the shirt", and the useful answer is the closest face rather than
 * a refusal.
 *
 * Distance is measured to the face rectangle, so a point inside one scores 0 and
 * wins outright.
 */
export function nearestFace(x: number, y: number): RegionId {
  let best: RegionId | null = null;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const id of Object.keys(REGIONS) as RegionId[]) {
    const r = REGIONS[id];
    const dx = Math.max(r.x - x, 0, x - (r.x + r.w));
    const dy = Math.max(r.y - y, 0, y - (r.y + r.h));
    const dist = dx * dx + dy * dy;
    // Strict `<` keeps ties going to the first face in REGION_IDS order, so the
    // same drop always lands the same way rather than depending on key order.
    if (dist < bestDist) {
      bestDist = dist;
      best = id;
    }
  }

  // REGIONS is never empty — assertRegionsMatch guarantees all 18 at startup.
  if (!best) throw new Error("no regions defined");
  return best;
}

/** Where a point sits inside a face, in placement coordinates. */
export function toFaceCoords(face: RegionId, x: number, y: number): Point {
  const r = REGIONS[face];
  return { x: (x - r.x) / r.w, y: (y - r.y) / r.h };
}
